import React, { useEffect, useMemo, useState } from 'react';
import { Divider, Table } from 'antd';
import { useTranslation, Trans } from 'react-i18next';
import PropTypes from 'prop-types';
import numeral from 'numeral';
import moment from 'moment';
import useAuthContext from '../../contexts/AuthContext';
import useErrorMessage from '../../utils/ErrorMessage';
import { arrayConditionnalElement } from '../../utils/arrayConditionnalElement';
import { MRIWK_MRI_BUILDING_TYPES } from './mriwkFields';

const SummaryModalQuote = ({ quote, formattedQuote = {} }) => {
  const { t } = useTranslation();
  const { dispatchAPI } = useAuthContext();
  const { message } = useErrorMessage();
  const { type, local } = quote;
  const [enums, setEnums] = useState({});
  const rcpFields = [
    'guarantee_limit',
    ...(['RCP', 'RCPCM', 'RCPWK'].includes(quote?.type)
      ? ['extended_legal_protection']
      : []),
    'exploitation_responsibility',
    ...(['RCP', 'RCPCM', 'RCPML'].includes(quote?.type)
      ? ['social_mandate_responsibility']
      : []),
    ...(quote?.type === 'RCPML'
      ? ['mobility_guarantee', 'legal_protection', 'extended_legal_protection']
      : [])
  ];

  const rcngawkFields = ['guarantee_package', 'extended_legal_protection'];
  const mrpTableKeys = [
    'fire_explosion_attack_climatic_event',
    'theft_vandalism',
    'water_damage',
    'broken_windows',
    'broken_machine',
    'electric_damage',
    'external_furniture',
    'chilled_merchandise'
  ];

  const mrpSwitches = [
    'archives_capital',
    'value_capital',
    'theft_capital',
    'lent_goods',
    'exploitation_responsibility',
    'extended_legal_protection_mrp',
    ...((!quote?.activities?.includes('Activité de bureau') &&
      quote.type === 'MRP') ||
    (!quote?.activities?.includes('Activité de bureau') &&
      quote.type === 'MRPCM')
      ? ['exploitation_loss', 'rc_pro', 'social_mandate_responsibility']
      : []),
    ...(!quote?.activities?.includes('Activité de bureau') &&
    quote.type === 'MRPWK'
      ? ['exploitation_loss', 'rc_pro']
      : []),
    ...(quote.type === 'MRPML'
      ? [
          'legal_protection_mrp',
          'additional_costs',
          'VTAM_choc_provisioire',
          'operation_of_premises',
          'object_of_value',
          'intoxication_alimentaire',
          'social_mandate_responsibility',
          'rc_pro_guarantee',
          'mobility_guarantee',
          'business_loss_guarantee',
          'market_value'
        ]
      : [])
  ];

  const assoTableKeys = [
    ...(local
      ? [
          'catastrophe',
          'tech_catastrophe',
          'archives_capital',
          'value_capital',
          'art_capital',
          'theft_capital',
          'owner_insurance',
          'fire_explosion_attack_climatic_event',
          'chilled_merchandise',
          'theft_vandalism',
          'water_damage',
          'broken_windows',
          'broken_machine',
          'electric_damage'
        ]
      : ['civil_responsibility_asso', 'local_insurance'])
  ];
  const assoSwitches = [
    ...(local === false
      ? [
          'social_mandate_responsibility',
          'extended_legal_protection',
          'classic_accident_insurance',
          'reinforced_accident_insurance'
        ]
      : [
          'civil_responsibility_asso',
          'light_inflatable_structure',
          'social_mandate_responsibility',
          'extended_legal_protection',
          'classic_accident_insurance',
          'reinforced_accident_insurance'
        ])
  ];
  const doSwitches = [
    'guarantee_limit',
    'employer_protection',
    'cyber_protection',
    'holding_protection',
    'key_person_protection'
  ];
  const rcpmkSwitches = [
    'guarantee_limit',
    'operational_protection',
    'cyber_events_protection',
    'key_man_protection',
    ...arrayConditionnalElement(
      'merge_protection',
      quote.activity_type === 'CONSULTING'
    ),
    'management_protection'
  ];

  const rcphxSwitches = [
    'guarantee_limit',
    ...arrayConditionnalElement(
      'turnover_usa_canada',
      quote.turnover_usa_canada && !['MISC','BIE','MED','OSTEO'].includes(quote.activity_type)
    ),
    'exploitation_responsibility',
    'legal_protection_rcphx',
    ...arrayConditionnalElement(
      'legal_protection_usa_canada',
      !['MISC','BIE','MED','OSTEO'].includes(quote.activity_type)
    )
  ];

  const MRIWKSwitches = [
    'guarantee_package',
    quote?.legal_protection ? 'legal_protection' : 'no_legal_protection',
    ...arrayConditionnalElement(
      {
        name: 'machine_breakdown',
        getter: ({ optionnal_guarantees = [] }) =>
          optionnal_guarantees?.includes?.('MACHINE_BREAKDOWN')
      },
      MRIWK_MRI_BUILDING_TYPES.includes(quote?.building_type)
    ),
    {
      name: 'solar_panels',
      getter: ({ optionnal_guarantees = [] }) =>
        optionnal_guarantees?.includes?.('SOLAR_PANELS')
    },
    {
      name: 'veranda',
      getter: ({ optionnal_guarantees = [] }) =>
        optionnal_guarantees?.includes?.('VERANDA')
    }
  ];

  const switchesMap = {
    DO: doSwitches,
    RCPMK: rcpmkSwitches,
    RCPHX: rcphxSwitches,
    MRIWK: MRIWKSwitches
  };

  const reducedDoSwitches = useMemo(() => {
    const defaultValue = {
      selected: [],
      notSelected: []
    };

    if (type !== 'DO') {
      return defaultValue;
    }

    return doSwitches.reduce((acc, doSwitchName) => {
      const value = quote[doSwitchName];
      const isSelected = !!value;

      if (isSelected) {
        acc.selected.push({
          value,
          key: doSwitchName
        });
      } else {
        acc.notSelected.push(doSwitchName);
      }

      return acc;
    }, defaultValue);
  }, [quote]);

  const reducedSwitches = useMemo(() => {
    const defaultValue = {
      selected: [],
      notSelected: []
    };

    const switches = switchesMap[type];
    if (!switches) {
      return defaultValue;
    }

    return switches.reduce((acc, data) => {
      let switchName;
      let value;
      let isSelected = false;

      if (typeof data === 'string') {
        switchName = data;
        value = quote[switchName];
        isSelected = !!value;
      } else if (typeof data === 'object') {
        switchName = data.name;
        value = data?.getter?.(quote) || false;
        isSelected = !!value;
      }

      if (isSelected) {
        acc.selected.push({
          value,
          key: switchName
        });
      } else {
        acc.notSelected.push(switchName);
      }

      return acc;
    }, defaultValue);
  }, [quote]);

  const getEnums = async () => {
    try {
      const { data } = await dispatchAPI('GET', { url: '/contracts/enums' });
      setEnums(data);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  useEffect(() => {
    (async () => {
      await getEnums();
    })();
    // eslint-disable-next-line
  }, []);

  return (
    <div>
      {type?.includes('RCP') &&
        !['RCPMK'].includes(type) &&
        !['RCPHX'].includes(type) && (
          <>
            <Divider orientation="left">
              {t('validate_quote.modal.selected')}
            </Divider>
            <ul style={{ color: 'var(--clientColor)' }}>
              {rcpFields
                .filter((element) => quote[element])
                .map((element) => {
                  if (element === 'guarantee_limit') {
                    return (
                      <li key={element}>
                        <strong>
                          {` ${numeral(quote[element]).format('0,0')}€ `}
                        </strong>
                        {` ${t('validate_quote.modal.base')}`}
                      </li>
                    );
                  }
                  if (element === 'mobility_guarantee') {
                    return (
                      <li key={element}>
                        {`${t('validate_quote.modal.mobility_guarantee')}`}
                        <strong>
                          {` ${numeral(quote[element]).format('0,0')}€ `}
                        </strong>
                        {`${t(
                          'validate_quote.modal.mobility_guarantee_price'
                        )}`}
                      </li>
                    );
                  }
                  if (quote[element] >= 250) {
                    return (
                      <li key={element}>
                        {t(
                          'validate_quote.modal.extended_legal_protection_no2'
                        )}
                        &nbsp;
                        <strong>
                          {t(
                            'validate_quote.modal.extended_legal_protection_no2_price'
                          )}
                        </strong>
                      </li>
                    );
                  }
                  if (quote[element] >= 100) {
                    return (
                      <li key={element}>
                        {t(
                          'validate_quote.modal.extended_legal_protection_no_part1'
                        )}
                        &nbsp;
                        <strong>
                          {t(
                            'validate_quote.modal.extended_legal_protection_no_part2'
                          )}
                        </strong>
                        &nbsp;
                        {t(
                          'validate_quote.modal.extended_legal_protection_no_part3'
                        )}
                        &nbsp;
                        <strong>
                          {t(
                            'validate_quote.modal.extended_legal_protection_no_part4'
                          )}
                        </strong>
                      </li>
                    );
                  }
                  return (
                    <li key={element}>
                      {t(
                        `validate_quote.modal.${
                          (element === 'legal_protection' ||
                            element === 'exploitation_responsibility') &&
                          type === 'RCPML'
                            ? `${element}_ml`
                            : `${element}`
                        }`
                      )}
                      &nbsp;
                      <strong>
                        {t(
                          `validate_quote.modal.${
                            (element === 'legal_protection' ||
                              element === 'extended_legal_protection_price' ||
                              element === 'exploitation_responsibility') &&
                            type === 'RCPML'
                              ? `${element}_ml_price`
                              : `${element}_price`
                          }`
                        )}
                      </strong>
                    </li>
                  );
                })}
              {type !== 'RCPML' && type !== 'MRPML' ? (
                <li>
                  {t(`validate_quote.modal.legal_protection`)}
                  &nbsp;
                  <strong>0,00€</strong>
                  {`) `}
                </li>
              ) : (
                <></>
              )}
              <li>
                <span>
                  {`La franchise sélectionnée par votre client(e) est de `}
                  <strong>{`${quote?.deductible_amount}€`}</strong>
                  {`, celle-ci lui
                semble convenable par rapport à son exposition et à son activité
                professionnelle`}
                </span>
              </li>
            </ul>
            <Divider orientation="left">
              {t('validate_quote.modal.options_not_selected')}
            </Divider>
            <ul style={{ color: 'var(--clientColor)' }}>
              {rcpFields
                .filter(
                  (element) => !quote[element] && element !== 'guarantee_limit'
                )
                .map((element) => {
                  return (
                    <li key={element}>
                      {t(
                        `validate_quote.modal.${
                          (element === 'legal_protection' ||
                            element === 'exploitation_responsibility') &&
                          type === 'RCPML'
                            ? `${element}_ml`
                            : `${element}`
                        }`
                      )}
                      &nbsp;
                      <strong>
                        {t(
                          `validate_quote.modal.${
                            (element === 'legal_protection' ||
                              element === 'extended_legal_protection_price' ||
                              element === 'exploitation_responsibility') &&
                            type === 'RCPML'
                              ? `${element}_ml_price`
                              : `${element}_price`
                          }`
                        )}
                      </strong>
                    </li>
                  );
                })}
            </ul>
          </>
        )}
      {type === 'RCNGAWK' && (
        <>
          <Divider orientation="left">
            {t('validate_quote.modal.selected')}
          </Divider>
          <ul style={{ color: 'var(--clientColor)' }}>
            {rcngawkFields
              .filter((element) => quote[element])
              .map((element) => {
                return (
                  <li key={element}>
                    {typeof quote[element] === 'boolean'
                      ? t(`validate_quote.modal.${element}`)
                      : t(`validate_quote.modal.${element}_${quote[element]}`)}
                    &nbsp;
                    <strong>
                      {typeof quote[element] === 'boolean'
                        ? t(`validate_quote.modal.${element}_price`)
                        : t(
                            `validate_quote.modal.${element}_${quote[element]}_price`
                          )}
                    </strong>
                  </li>
                );
              })}
            <li>
              {t(`validate_quote.modal.legal_protection`)}
              &nbsp;
              <strong>0,00€</strong>
              {`) `}
            </li>
          </ul>
          <Divider orientation="left">
            {t('validate_quote.modal.options_not_selected')}
          </Divider>
          <ul style={{ color: 'var(--clientColor)' }}>
            {rcngawkFields
              .filter(
                (element) =>
                  !quote[element] ||
                  (element === 'guarantee_package' &&
                    quote[element] !== 'PKG_4')
              )
              .map((element) => {
                if (element === 'guarantee_package') {
                  if (quote[element] === 'PKG_2') {
                    return (
                      <>
                        <li key={`${element}_PKG_3`}>
                          {t(`validate_quote.modal.guarantee_package_PKG_3`)}
                          &nbsp;
                          <strong>
                            {t(
                              `validate_quote.modal.guarantee_package_PKG_3_price`
                            )}
                          </strong>
                        </li>
                        <li key={`${element}_PKG_4`}>
                          {t(`validate_quote.modal.guarantee_package_PKG_4`)}
                          &nbsp;
                          <strong>
                            {t(
                              `validate_quote.modal.guarantee_package_PKG_4_price`
                            )}
                          </strong>
                        </li>
                      </>
                    );
                  }
                  if (quote[element] === 'PKG_3') {
                    return (
                      <li key={`${element}_PKG_4`}>
                        {t(`validate_quote.modal.guarantee_package_PKG_4`)}
                        &nbsp;
                        <strong>
                          {t(
                            `validate_quote.modal.guarantee_package_PKG_4_price`
                          )}
                        </strong>
                      </li>
                    );
                  }
                } else {
                  return (
                    <li key={element}>
                      {typeof quote[element] === 'boolean'
                        ? t(`validate_quote.modal.${element}`)
                        : t(
                            `validate_quote.modal.${element}_${quote[element]}`
                          )}
                      &nbsp;
                      <strong>
                        {typeof quote[element] === 'boolean'
                          ? t(`validate_quote.modal.${element}_price`)
                          : t(
                              `validate_quote.modal.${element}_${quote[element]}_price`
                            )}
                      </strong>
                    </li>
                  );
                }
              })}
          </ul>
        </>
      )}
      {type === 'MRI' && (
        <>
          <Divider orientation="left">
            {t('validate_quote.modal.selected')}
          </Divider>
          <ul style={{ color: 'var(--clientColor)' }}>
            <strong>{t(`validate_quote.modal.guarantees_selected`)}</strong>
            {enums.guaranteeDetails &&
              enums.guaranteeDetails
                .filter((f) => {
                  if (quote.guarantee_type === 'TYPE1')
                    return f.startsWith('g1');

                  if (quote.guarantee_type === 'TYPE2')
                    return f.startsWith('2', 2);

                  return f.startsWith('3', 3);
                })
                .map((e) => (
                  <li key={e}>
                    {t(`contracts.form.${e}`)}
                    &nbsp;
                  </li>
                ))}
            {quote?.selected_options?.includes('machine_breakdown') && (
              <li>{`${t('contracts.form.machine_breakdown')} `}</li>
            )}
            {quote?.selected_options?.includes('indoor_pool') && (
              <li>{`${t('contracts.form.indoor_pool')} `}</li>
            )}
            {quote?.selected_options?.includes('outdoor_pool') && (
              <li>{`${t('contracts.form.outdoor_pool')} `}</li>
            )}
            {quote?.selected_options?.includes('solar_panels') && (
              <li>{`${t('contracts.form.solar_panels')} `}</li>
            )}
            {quote?.selected_options?.includes('veranda') && (
              <li>{`${t('contracts.form.veranda')} `}</li>
            )}
          </ul>
          <Divider orientation="left">
            {t('validate_quote.modal.guarantees_not_selected')}
          </Divider>
          <ul style={{ color: 'var(--clientColor)' }}>
            {!quote?.selected_options?.includes('machine_breakdown') && (
              <li>{`${t('contracts.form.machine_breakdown')} `}</li>
            )}
            {!quote?.selected_options?.includes('indoor_pool') && (
              <li>{`${t('contracts.form.indoor_pool')} `}</li>
            )}
            {!quote?.selected_options?.includes('outdoor_pool') && (
              <li>{`${t('contracts.form.outdoor_pool')} `}</li>
            )}
            {!quote?.selected_options?.includes('solar_panels') && (
              <li>{`${t('contracts.form.solar_panels')} `}</li>
            )}
            {!quote?.selected_options?.includes('veranda') && (
              <li>{`${t('contracts.form.veranda')} `}</li>
            )}
            {enums.guaranteeDetails &&
              enums.guaranteeDetails
                .filter((f) => {
                  if (quote.guarantee_type === 'TYPE1')
                    return !f.startsWith('g1');

                  if (quote.guarantee_type === 'TYPE2')
                    return !f.startsWith('2', 2);

                  return !f.startsWith('3', 3);
                })
                .map((e) => (
                  <li key={e}>
                    {t(`contracts.form.${e}`)}
                    &nbsp;
                  </li>
                ))}
          </ul>
        </>
      )}
      {type?.includes('MRP') && (
        <>
          <Divider orientation="left">
            {t('validate_quote.modal.selected')}
          </Divider>
          <Table
            rowKey="label"
            dataSource={mrpTableKeys
              .filter((key) => quote[key])
              .map((key) => ({
                label: key,
                value: quote[key]
              }))}
            columns={[
              {
                title:
                  'Les garanties & capitaux ci-dessous qui lui semblent suffisants :',
                dataIndex: 'label',
                render: (label) => t(`contracts.form.${label}`)
              },
              {
                dataIndex: 'value',
                render: (value) => `${numeral(value).format('0,0')} €`
              }
            ]}
            pagination={false}
          />
          <p>
            Les garanties attentats et acte de terrorisme, catastrophe
            naturelles, catastrophes technologiques, responsabilité locative,
            recours des voisins et des tiers, recours des locataires, défense
            pénale et recours sont incluses dans l&apos;offre.
          </p>
          {quote?.type !== 'MRPML' && (
            <p>
              <strong>{t(`validate_quote.modal.legal_protection`)}</strong>
            </p>
          )}
          <p>
            La franchise sélectionnée par votre client(e) est de
            <strong>{` ${quote.deductible_amount} €`}</strong>
          </p>
          {mrpSwitches.filter((k) => quote[k]).length ? (
            <>
              <p>Votre client(e) a souscrit à :</p>
              <ul>
                {mrpSwitches
                  .filter((k) => quote[k])
                  .map((k) => (
                    <li key={k}>
                      {t(`validate_quote.modal.mrp.switches.${k}`)}
                      <strong>
                        {` ${t(
                          `validate_quote.modal.mrp.switches_cost.${k}${
                            type === 'MRPML' ? '_ml' : ''
                          }`
                        )}`}
                      </strong>
                    </li>
                  ))}
              </ul>
            </>
          ) : (
            ''
          )}
          {mrpSwitches.filter((k) => !quote[k]).length ? (
            <>
              <p>
                Votre client(e) n&apos;a pas souhaité souscrire les options
                suivantes:
              </p>
              <ul>
                {mrpSwitches
                  .filter((k) => !quote[k])
                  .map((k) => (
                    <li key={k}>
                      {t(`validate_quote.modal.mrp.switches.${k}`)}
                      <strong>
                        {` ${t(
                          `validate_quote.modal.mrp.switches_cost.${k}${
                            type === 'MRPML' ? '_ml' : ''
                          }`
                        )}`}
                      </strong>
                    </li>
                  ))}
              </ul>
            </>
          ) : (
            ''
          )}
          <p>
            {`Votre client(e) a déclaré au cours des 36 derniers mois ${
              quote.termination_by_company
                ? `une résiliation pour ${t(
                    `contracts.form.${quote.termination_reason_by_company}`
                  )}`
                : 'aucune résiliation compagnie'
            } et ${
              quote.claims.length
                ? `${quote.claims.map(
                    (c) =>
                      `1 sinistre ${c.claim_type} au ${moment(
                        c.claim_date
                      ).format('LL')} (${numeral(c.claim_amount).format(
                        '0,0'
                      )} €)`
                  )}`
                : 'aucun sinistre.'
            }`}
          </p>
          <p>Votre client(e) souhaite assurer :</p>
          <ul>
            {quote.sites.map((s) =>
              s && s.address ? (
                <li key={s._id}>
                  {`Un site (${!s.isolated_site ? 'non ' : ''}isolé) au ${
                    s.address.number
                  } ${s.address.street} ${s.address.postal_code} ${
                    s.address.city
                  }${
                    s.resident_status
                      ? ` en sa qualité de ${t(
                          `contracts.form.options.${s.resident_status}`
                        )}${
                          s.resident_status === 'TENANT' && s.tenant_status
                            ? ` (${t(
                                `contracts.form.options.${s.tenant_status}`
                              )})`
                            : ''
                        }`
                      : ''
                  } ${
                    s.surface_area
                      ? ` pour une superficie de ${s.surface_area} M2.`
                      : ''
                  }`}
                </li>
              ) : (
                ''
              )
            )}
          </ul>
        </>
      )}
      {type === 'ASSOCIATION' && (
        <>
          <Divider orientation="left">
            {t('validate_quote.modal.selected')}
          </Divider>
          <Table
            rowKey="label"
            dataSource={assoTableKeys
              .filter((key) => quote[key])
              .map((key) => ({
                label: key,
                value: quote[key]
              }))}
            columns={[
              {
                title:
                  'Les garanties & capitaux ci-dessous qui lui semblent suffisants :',
                dataIndex: 'label',
                render: (label) => t(`contracts.form.${label}`)
              },
              {
                dataIndex: 'value',
                render: (value) =>
                  !local ? `` : `${numeral(value).format('0,0')} €`
              }
            ]}
            pagination={false}
          />
          <p>
            Les garanties attentats et acte de terrorisme, catastrophe
            naturelles, catastrophes technologiques, responsabilité locative,
            recours des voisins et des tiers, recours des locataires, défense
            pénale et recours sont incluses dans l&apos;offre.
          </p>
          <p>
            <strong>{t(`validate_quote.modal.legal_protection`)}</strong>
          </p>
          <p>
            La franchise sélectionnée par votre client(e) est de
            <strong>{` ${quote.deductible_amount} €`}</strong>
          </p>
          {assoSwitches.filter((k) => quote[k]).length ? (
            <>
              <p>Votre client(e) a souscrit à :</p>
              <ul>
                {assoSwitches
                  .filter((k) => quote[k])
                  .map((k) => (
                    <li key={k}>
                      {t(`validate_quote.modal.asso.switches.${k}`)}
                      <strong>
                        {` ${t(
                          `validate_quote.modal.asso.switches_cost.${k}`
                        )}`}
                      </strong>
                    </li>
                  ))}
              </ul>
            </>
          ) : (
            ''
          )}
          {assoSwitches.filter((k) => !quote[k]).length ? (
            <>
              <p>
                Votre client(e) n&apos;a pas souhaité souscrire les options
                suivantes:
              </p>
              <ul>
                {assoSwitches
                  .filter((k) => !quote[k])
                  .map((k) => (
                    <li key={k}>
                      {t(`validate_quote.modal.asso.switches.${k}`)}
                      <strong>
                        {` ${t(
                          `validate_quote.modal.asso.switches_cost.${k}`
                        )}`}
                      </strong>
                    </li>
                  ))}
              </ul>
            </>
          ) : (
            ''
          )}
          <p>
            {`Votre client(e) a déclaré au cours des 36 derniers mois ${
              quote.termination_by_company
                ? `une résiliation pour ${t(
                    `contracts.form.${quote.termination_reason_by_company}`
                  )}`
                : 'aucune résiliation compagnie'
            } et ${
              quote.claims.length
                ? `${quote.claims.map(
                    (c) =>
                      `1 sinistre ${c.claim_type} au ${moment(
                        c.claim_date
                      ).format('LL')} (${numeral(c.claim_amount).format(
                        '0,0'
                      )} €)`
                  )}`
                : 'aucun sinistre.'
            }`}
          </p>
          {quote.local ? <p>Votre client(e) souhaite assurer :</p> : ''}
          <ul>
            {quote.sites.map((s) =>
              s && s.address ? (
                <li key={s._id}>
                  {`Un site (${!s.isolated_site ? 'non ' : ''}isolé) au ${
                    s.address.number
                  } ${s.address.street} ${s.address.postal_code} ${
                    s.address.city
                  }${
                    s.resident_status
                      ? ` en sa qualité de ${t(
                          `contracts.form.options.${s.resident_status}`
                        )}${
                          s.resident_status === 'TENANT' && s.tenant_status
                            ? ` (${t(
                                `contracts.form.options.${s.tenant_status}`
                              )})`
                            : ''
                        }`
                      : ''
                  } ${
                    s.surface_area
                      ? ` pour une superficie de ${s.surface_area} M2.`
                      : ''
                  }`}
                </li>
              ) : (
                ''
              )
            )}
          </ul>
        </>
      )}
      {Object.keys(switchesMap).includes(type) && (
        <>
          {!!reducedSwitches.selected.length && (
            <>
              <Divider orientation="left">
                {t('validate_quote.modal.selected')}
              </Divider>
              <ul style={{ color: 'var(--clientColor)' }}>
                {reducedSwitches.selected.map(({ key, value }) => (
                  <Trans>
                    <li key={key}>
                      {t(`validate_quote.modal.${type?.toLowerCase()}.${key}`, {
                        ...(formattedQuote || {}),
                        value
                      })}
                    </li>
                  </Trans>
                ))}
              </ul>
            </>
          )}

          {!!reducedSwitches.notSelected.length && (
            <>
              <Divider orientation="left">
                {t('validate_quote.modal.options_not_selected')}
              </Divider>
              <ul style={{ color: 'var(--clientColor)' }}>
                {reducedSwitches.notSelected.map((key) => (
                  <Trans>
                    <li key={key}>
                      {t(`validate_quote.modal.${type?.toLowerCase()}.${key}`)}
                    </li>
                  </Trans>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </div>
  );
};

SummaryModalQuote.propTypes = {
  quote: PropTypes.shape({
    activity_detail: PropTypes.array,
    selected_options: PropTypes.array,
    guarantee_type: PropTypes.string,
    deductible_amount: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    guarantee_limit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    termination_by_company: PropTypes.bool,
    termination_reason_by_company: PropTypes.string,
    claims: PropTypes.arrayOf(
      PropTypes.shape({
        claim_type: PropTypes.string
      })
    ),
    legal_protection: PropTypes.bool,
    turnover_usa_canada: PropTypes.number,
    activity_type: PropTypes.string,
    sites: PropTypes.arrayOf(
      PropTypes.shape({
        isolated_site: PropTypes.bool,
        resident_status: PropTypes.string,
        tenant_status: PropTypes.string
      })
    ),
    type: PropTypes.string,
    local: PropTypes.bool,
    activities: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired
};

export default SummaryModalQuote;
